import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiBarChart2, FiX, FiPrinter, FiRefreshCw, FiDownload, FiSettings } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import PdfOptionsModal from '../../pdf/PdfOptionsModal';
import { usePdfDownload } from '../../pdf/usePdfDownload';
import {
  getOfferings,
  getDonationCategories,
  getFamilies,
  getMyFamily,
  getUsers,
  getDerasarPersons,
  getDerasarPersonsAdmin,
  exportReportTable,
  getAnonymousReceipts,
  getReceipts,
  getOfferingsReport,
  getMyOfferingsReport,
} from '../../services/api';
import { buildMemberExportPayload } from '../../utils/reportExportPayload';
import { buildMemberFamilyMap, resolveFamilyGroup, offeringOwner } from '../../utils/familyReportGrouping';
import { PageHeader, ExcelExportModal } from '../../components/common';
import MemberContributionsReport from './reports/MemberContributionsReport';
import PendingBalanceReport from './reports/PendingBalanceReport';
import MemberLedgerReport from './reports/MemberLedgerReport';
import FamilyReport from './reports/FamilyReport';
import AnonymousDonationsReport from './reports/AnonymousDonationsReport';
import FullInfoReport from './reports/FullInfoReport';
import OfferingAgingReport from './reports/OfferingAgingReport';
import OwnerLedgerReport from './reports/OwnerLedgerReport';
import OfferingsReport from '../Reports/reports/OfferingsReport';
import OfferingsMemberSelect from '../Offerings/OfferingsMemberSelect';
import styles from './MemberReports.module.css';

const subtractOneDay = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const getDefaultDatesForFy = (value, fys) => {
  if (!value) return { from: '', to: '' };
  if (value === 'opening') {
    const firstFy = [...fys].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''))[0];
    return { from: '', to: firstFy?.startDate ? subtractOneDay(firstFy.startDate) : '' };
  }
  const fy = fys.find((y) => String(y.id) === value);
  if (!fy) return { from: '', to: '' };
  return { from: fy.startDate || '', to: fy.endDate || '' };
};

const TABS = [
  { id: 'offerings',     label: 'Offerings'     },
  { id: 'contributions', label: 'Contributions' },
  { id: 'pending-balance', label: 'Pending Balance' },
  { id: 'offering-aging', label: 'Offering Aging' },
  { id: 'member-ledger', label: 'Member Ledger' },
  { id: 'owner-ledger', label: 'Owner Ledger', adminOnly: true },
  { id: 'family-report', label: 'Family Report' },
  { id: 'full-info', label: 'Receipts Book' },
  { id: 'anonymous-donations', label: 'Shah A/C Donations', adminOnly: true },
];

const mName = (offering) => offering.member?.name || offering.userId?.name || '-';
const mCat  = (o) => o.DonationCategory?.name || o.donationCategoryId?.name || o.donationCategory?.name || '-';
const mDays = (o) => Math.floor((Date.now() - new Date(o.offeringDate)) / 86_400_000);
const personId = (item) => String(item.userId?.id ?? item.userId ?? '');

const MemberReportsPage = () => {
  const {
    activeDerasarId,
    activeDerasar,
    user,
    openingFinancialYear,
    visibleFinancialYears,
    activeFinancialYearId,
  } = useAuth();
  const isUserOnly = user?.role === 'user';

  const [activeTab, setActiveTab] = useState('offerings');
  const [viewMode, setViewMode] = useState('detail');
  const [offerings, setOfferings] = useState([]);
  const [years, setYears] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFyId, setOpenFyId] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterMemberId, setFilterMemberId] = useState('');
  const [selectedLedgerMember, setSelectedLedgerMember] = useState('');
  const [selectedOwnerMember, setSelectedOwnerMember] = useState('');
  const [dependents, setDependents] = useState([]);
  const [families, setFamilies] = useState([]);
  const [ownOfferings, setOwnOfferings] = useState([]);
  const [showFamilyAll, setShowFamilyAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [allDeps, setAllDeps] = useState([]);
  const [anonymousReceipts, setAnonymousReceipts] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [ownReceipts, setOwnReceipts] = useState([]);

  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo,   setFilterTo]   = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo,   setAppliedTo]   = useState('');

  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);

  // Offerings tab — independent backend fetch with its own filters
  const [offeringsReportData, setOfferingsReportData] = useState(null);
  const [offeringsLoading, setOfferingsLoading] = useState(false);
  const [offeringsStatus, setOfferingsStatus] = useState('');
  const offeringsFiltersRef = useRef({});
  offeringsFiltersRef.current = { filterYear, filterFrom, filterTo, offeringsStatus };
  const offeringsReadyRef = useRef(false);

  const fetchOfferingsReport = useCallback(async () => {
    if (!activeDerasarId) return;
    const { filterYear: yr, filterFrom: from, filterTo: to, offeringsStatus: st } = offeringsFiltersRef.current;
    setOfferingsLoading(true);
    try {
      const params = {
        derasarId: activeDerasarId,
        ...(yr   ? { yearId: yr }     : {}),
        ...(from  ? { fromDate: from } : {}),
        ...(to    ? { toDate: to }     : {}),
        ...(st    ? { status: st }     : {}),
      };
      const fetchFn = user?.role === 'user' ? getMyOfferingsReport : getOfferingsReport;
      const res = await fetchFn(params);
      setOfferingsReportData(res?.data ?? null);
    } catch {
      toast.error('Failed to load offerings report');
    } finally {
      setOfferingsLoading(false);
    }
  }, [activeDerasarId, user?.role]);

  const TAB_TO_PDF_TYPE = {
    'offerings':           'offerings',
    'contributions':       'memberContributions',
    'pending-balance':     'pendingBalance',
    'offering-aging':      'offeringAging',
    'member-ledger':       'memberLedger',
    'owner-ledger':        'ownerLedger',
    'family-report':       'familyReport',
    'full-info':           'receiptsBook',
    'anonymous-donations': 'anonymousDonations',
  };
  const activePdfType = TAB_TO_PDF_TYPE[activeTab];

  const { download: downloadPdf, downloading: pdfDownloading, hasSaved: pdfHasDefaults } = usePdfDownload(activePdfType);

  const yearOptions = useMemo(() => {
    if (!openingFinancialYear) return years;
    const hasOpening = years.some((year) => String(year.id) === String(openingFinancialYear.id));
    return hasOpening ? years : [...years, openingFinancialYear];
  }, [years, openingFinancialYear]);

  const selectedFy = useMemo(
    () => years.find((y) => String(y.id) === filterYear) || null,
    [years, filterYear]
  );
  const fyMinDate = selectedFy?.startDate || '';
  const fyMaxDate = selectedFy?.endDate   || '';

  const ledgerMemberOptions = useMemo(() => {
    if (isUserOnly) {
      const options = [
        {
          id: String(user.id),
          name: `${user.name} (You)`,
          email: user.email || '',
          phone: user.phone || '',
          badge: 'You',
        },
      ];

      dependents.forEach((dependent) => {
        const dependentId = String(dependent.id ?? dependent.userId);
        if (!options.find((option) => option.id === dependentId)) {
          options.push({
            id: dependentId,
            name: dependent.name,
            email: user.email || '',
            phone: user.phone || '',
            badge: dependent.relation || 'Dependent',
          });
        }
      });

      if (families.length > 0) {
        const family = families[0];

        if (family.head?.id && String(family.head.id) !== String(user.id) && !options.find((option) => option.id === String(family.head.id))) {
          options.push({
            id: String(family.head.id),
            name: family.head.name,
            email: family.head.email || '',
            phone: family.head.phone || '',
            badge: 'Head',
          });
        }

        (family.members || [])
          .filter((member) => member.status === 'approved')
          .forEach((member) => {
            const memberId = String(member.member?.id ?? member.userId);
            if (!options.find((option) => option.id === memberId)) {
              options.push({
                id: memberId,
                name: member.member?.name || '-',
                email: member.member?.email || '',
                phone: member.member?.phone || '',
                badge: 'Member',
              });
            }
          });
      }

      return options.sort((a, b) => a.name.localeCompare(b.name));
    }

    return allDeps
      .map((dep) => ({
        id: dep.id,
        name: dep.name,
        badge: `Person of ${dep.createdByMember?.name || 'Member'}`,
        email: dep.createdByMember?.auth?.email || '',
        phone: dep.createdByMember?.phone || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [isUserOnly, families, user, dependents, allDeps]);

  const filterMemberName = useMemo(
    () => filterMemberId ? (ledgerMemberOptions.find((m) => String(m.id) === filterMemberId)?.name || '') : '',
    [filterMemberId, ledgerMemberOptions]
  );

  const memberPdfProps = useMemo(() => {
    const base = { derasar: activeDerasar?.derasarId };
    const n = (v) => Number(v || 0);
    const pName  = (o) => o.member?.name || o.userId?.name || '-';
    const pCat   = (o) => o.DonationCategory?.name || (typeof o.donationCategoryId === 'object' ? o.donationCategoryId?.name : null) || o.donationCategory?.name || '-';
    const pDays  = (o) => Math.floor((Date.now() - new Date(o.offeringDate)) / 86_400_000);

    // Apply the same filters as shown on screen
    const byFY  = (o) => !filterYear || String(o.financialYearId?.id ?? o.financialYearId) === filterYear;
    const byCat = (o) => !filterCategoryId || String(o.donationCategoryId?.id ?? o.donationCategoryId) === filterCategoryId;
    const byMem = (o) => !filterMemberId || String(o.userId?.id ?? o.userId) === filterMemberId;
    const byReceiptFY  = (r) => !filterYear || String(r.financialYearId?.id ?? r.financialYearId) === String(filterYear);
    const byReceiptCat = (r) => !filterCategoryId || String(r.donationCategoryId?.id ?? r.donationCategoryId) === filterCategoryId;

    switch (activeTab) {
      case 'offerings': {
        const all = (offeringsReportData?.rows) || [];
        const rows = filterMemberName
          ? all.filter((r) => (r.memberName || '').toLowerCase().includes(filterMemberName.toLowerCase()))
          : all;
        const active = rows.filter((r) => r.status !== 'cancelled');
        const summary = filterMemberName ? {
          totalOffered: active.reduce((s, r) => s + n(r.offeredAmount), 0),
          totalPaid:    active.reduce((s, r) => s + n(r.paidAmount), 0),
          totalPending: active.reduce((s, r) => s + n(r.pendingAmount), 0),
        } : (offeringsReportData?.summary || {});
        const fyLabelForFilter = filterYear === 'opening'
          ? 'Opening'
          : (years.find((y) => String(y.id) === filterYear)?.yearLabel || 'All Years');
        const filtersLine = [
          `Financial Year: ${fyLabelForFilter}`,
          filterFrom ? `From: ${filterFrom}` : '',
          filterTo ? `To: ${filterTo}` : '',
        ].filter(Boolean).join('  |  ');
        return { ...base, rows, summary, filters: filtersLine };
      }

      case 'contributions': {
        const getLastPaidDate = (o) => {
          const dates = (o.settlements || [])
            .map((s) => s.Receipt?.receiptDate)
            .filter(Boolean)
            .sort((a, b) => (a > b ? 1 : -1));
          return dates.length > 0 ? dates[dates.length - 1] : null;
        };
        const relevant = offerings.filter((o) => byFY(o) && byCat(o) && byMem(o) && n(o.paidAmount) > 0 && ['partial', 'completed'].includes(o.status));
        const detailRows = relevant.map((o) => ({
          userId:        String(o.userId?.id ?? o.userId),
          memberName:    pName(o),
          category:      pCat(o),
          offeringDate:  o.offeringDate,
          paidDate:      getLastPaidDate(o),
          offeredAmount: n(o.offeredAmount),
          paidAmount:    n(o.paidAmount),
          pendingAmount: n(o.pendingAmount),
        }));
        const summaryMap = {};
        relevant.forEach((o) => {
          const key = String(o.userId?.id ?? o.userId);
          if (!summaryMap[key]) summaryMap[key] = { memberName: pName(o), offeredAmount: 0, paidAmount: 0 };
          summaryMap[key].offeredAmount += n(o.offeredAmount);
          summaryMap[key].paidAmount    += n(o.paidAmount);
        });
        const summaryRows = Object.values(summaryMap).sort((a, b) => a.memberName.localeCompare(b.memberName));
        const fyLabelForFilter = filterYear === 'opening'
          ? 'Opening'
          : (years.find((y) => String(y.id) === filterYear)?.yearLabel || 'All Years');
        const filtersLine = [
          `Financial Year: ${fyLabelForFilter}`,
          appliedFrom ? `From: ${appliedFrom}` : '',
          appliedTo ? `To: ${appliedTo}` : '',
        ].filter(Boolean).join('  |  ');
        return { ...base, viewMode, rows: detailRows, summaryRows, filters: filtersLine };
      }

      case 'pending-balance': {
        const relevant = offerings.filter((o) => byFY(o) && byCat(o) && n(o.pendingAmount) > 0 && ['pending', 'partial'].includes(o.status));
        const detailRows = relevant.map((o) => ({
          userId:        String(o.userId?.id ?? o.userId),
          memberName:    pName(o),
          category:      pCat(o),
          offeringDate:  o.offeringDate,
          offeredAmount: n(o.offeredAmount),
          paidAmount:    n(o.paidAmount),
          pendingAmount: n(o.pendingAmount),
          status:        o.status,
        }));
        const summaryMap = {};
        relevant.forEach((o) => {
          const key = String(o.userId?.id ?? o.userId);
          if (!summaryMap[key]) summaryMap[key] = { memberName: pName(o), offeredAmount: 0, paidAmount: 0, pendingAmount: 0 };
          summaryMap[key].offeredAmount  += n(o.offeredAmount);
          summaryMap[key].paidAmount     += n(o.paidAmount);
          summaryMap[key].pendingAmount  += n(o.pendingAmount);
        });
        const summaryRows = Object.values(summaryMap).sort((a, b) => b.pendingAmount - a.pendingAmount);
        const fyLabelForFilter = filterYear === 'opening'
          ? 'Opening'
          : (years.find((y) => String(y.id) === filterYear)?.yearLabel || 'All Years');
        const filtersLine = [
          `Financial Year: ${fyLabelForFilter}`,
          appliedFrom ? `From: ${appliedFrom}` : '',
          appliedTo ? `To: ${appliedTo}` : '',
        ].filter(Boolean).join('  |  ');
        return { ...base, viewMode, rows: detailRows, summaryRows, filters: filtersLine };
      }

      case 'offering-aging': {
        const fyLabelForFilter = filterYear === 'opening'
          ? 'Opening'
          : (years.find((y) => String(y.id) === filterYear)?.yearLabel || 'All Years');
        const filtersLine = [
          `Financial Year: ${fyLabelForFilter}`,
          appliedFrom ? `From: ${appliedFrom}` : '',
          appliedTo ? `To: ${appliedTo}` : '',
        ].filter(Boolean).join('  |  ');
        return {
          ...base,
          filters: filtersLine,
          rows: offerings
            .filter((o) => byFY(o) && byCat(o) && byMem(o) && ['pending', 'partial'].includes(o.status) && n(o.pendingAmount) > 0)
            .map((o) => ({
              donorName:     pName(o),
              category:      pCat(o),
              pledgeDate:    o.offeringDate,
              pledgedAmount: n(o.offeredAmount),
              paidAmount:    n(o.paidAmount),
              pendingAmount: n(o.pendingAmount),
              daysPending:   pDays(o),
            }))
            .sort((a, b) => b.daysPending - a.daysPending),
        };
      }

      case 'member-ledger': {
        if (!selectedLedgerMember) return { ...base };
        const allMemberOfs = offerings.filter((o) => String(o.userId?.id ?? o.userId) === String(selectedLedgerMember));
        const pendingOfs = allMemberOfs
          .filter((o) => o.status === 'pending' || o.status === 'partial')
          .map((o) => ({ ...o, _categoryName: pCat(o) }))
          .sort((a, b) => (a.offeringDate || '').localeCompare(b.offeringDate || ''));
        const paidOfs = allMemberOfs
          .filter((o) => {
            const fyId = String(o.financialYearId?.id ?? o.financialYearId ?? '');
            return String(activeFinancialYearId) === fyId && n(o.paidAmount) > 0;
          })
          .map((o) => ({ ...o, _categoryName: pCat(o) }));
        const memberLabel = allMemberOfs.length > 0 ? pName(allMemberOfs[0]) : '';
        const fyLabel = years.find((y) => String(y.id) === String(activeFinancialYearId))?.yearLabel || 'Current Year';
        return {
          ...base,
          memberName: memberLabel,
          fyLabel,
          pendingOfferings: pendingOfs,
          paidOfferings: paidOfs,
          totalPending: pendingOfs.reduce((s, o) => s + n(o.pendingAmount), 0),
          totalPaid: paidOfs.reduce((s, o) => s + n(o.paidAmount), 0),
        };
      }

      case 'owner-ledger': {
        const ownerPersonIds = new Set(
          allDeps.filter((p) => String(p.createdByMemberId) === String(selectedOwnerMember)).map((p) => String(p.id))
        );
        const ownerOfs = offerings.filter(byFY).filter((o) => ownerPersonIds.has(String(o.userId?.id ?? o.userId)));
        const ownerName = allDeps.find((d) => d.createdByMember && String(d.createdByMember.id) === String(selectedOwnerMember))?.createdByMember?.name || '';
        const fyLabel = years.find((y) => String(y.id) === String(filterYear))?.yearLabel || (filterYear ? `FY ${filterYear}` : 'All Years');
        const personMap = {};
        ownerOfs.forEach((o) => {
          const key = String(o.userId?.id ?? o.userId);
          if (!personMap[key]) personMap[key] = { name: pName(o), count: 0, pledged: 0, paid: 0, pending: 0 };
          personMap[key].count++;
          personMap[key].pledged += n(o.offeredAmount);
          personMap[key].paid    += n(o.paidAmount);
          personMap[key].pending += n(o.pendingAmount);
        });
        return {
          ...base,
          ownerName,
          fyLabel,
          byPerson:       Object.values(personMap).sort((a, b) => a.name.localeCompare(b.name)),
          ownerOfferings: ownerOfs,
          totalPledged:   ownerOfs.reduce((s, o) => s + n(o.offeredAmount), 0),
          totalPaid:      ownerOfs.reduce((s, o) => s + n(o.paidAmount),    0),
          totalPending:   ownerOfs.reduce((s, o) => s + n(o.pendingAmount), 0),
          totalEntries:   ownerOfs.length,
        };
      }

      case 'family-report': {
        const familyMap = buildMemberFamilyMap(families);
        const groups = {};
        offerings
          .filter((o) => !filterYear || String(o.financialYearId?.id ?? o.financialYearId) === filterYear)
          .forEach((o) => {
            const uid = String(o.userId?.id ?? o.userId);
            const owner = offeringOwner(o);
            const { key, label } = resolveFamilyGroup(owner.id, owner.name, familyMap);
            if (!groups[key]) groups[key] = { label, memberMap: {}, pledged: 0, paid: 0, pending: 0 };
            groups[key].pledged += n(o.offeredAmount);
            groups[key].paid    += n(o.paidAmount);
            groups[key].pending += n(o.pendingAmount);
            if (!groups[key].memberMap[uid]) groups[key].memberMap[uid] = { name: pName(o), pledged: 0, paid: 0, pending: 0 };
            groups[key].memberMap[uid].pledged += n(o.offeredAmount);
            groups[key].memberMap[uid].paid    += n(o.paidAmount);
            groups[key].memberMap[uid].pending += n(o.pendingAmount);
          });
        const familyGroups = Object.values(groups)
          .map(({ memberMap, ...g }) => ({
            ...g,
            members: Object.values(memberMap).sort((a, b) => a.name.localeCompare(b.name)),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        return { ...base, familyGroups };
      }

      case 'full-info': {
        const filteredReceipts = receipts
          .filter(byReceiptFY)
          .filter(byReceiptCat)
          .filter((r) => !filterMemberId || String(r.userId?.id ?? r.userId) === filterMemberId);
        const getCatAmt = (r, catId) => {
          if (r.pledgeSettlements?.length > 0) {
            const sum = r.pledgeSettlements.reduce((t, s) => {
              const scId = s.Offering?.donationCategoryId ?? s.offering?.donationCategoryId;
              return String(scId) === String(catId) ? t + (parseFloat(s.settledAmount) || 0) : t;
            }, 0);
            if (sum > 0) return sum;
          }
          const rCatId = r.donationCategoryId?.id ?? r.donationCategoryId ?? r.DonationCategory?.id;
          return rCatId && String(rCatId) === String(catId) ? parseFloat(r.amountInr) || 0 : 0;
        };
        return {
          ...base,
          categories,
          rows: filteredReceipts.map((r) => ({
            receiptDate: r.receiptDate,
            receiptNo:   r.receiptNo,
            memberName:  r.member?.name || r.donorName || '-',
            paymentMode: r.paymentMode,
            amountInr:   n(r.amountInr),
            catAmounts:  categories.reduce((acc, cat) => ({ ...acc, [String(cat.id)]: getCatAmt(r, cat.id) }), {}),
          })),
          totalAmount: filteredReceipts.reduce((s, r) => s + n(r.amountInr), 0),
        };
      }

      case 'anonymous-donations': {
        const filteredAnon = anonymousReceipts.filter(byReceiptFY);
        return {
          ...base,
          filtered:    filteredAnon,
          totalAmount: filteredAnon.reduce((s, r) => s + n(r.amountInr), 0),
        };
      }

      default:
        return { ...base, rows: offerings };
    }
  }, [activeTab, offerings, receipts, anonymousReceipts, families, activeDerasar, filterYear, filterFrom, filterTo, appliedFrom, appliedTo, filterCategoryId, filterMemberId, viewMode, selectedLedgerMember, selectedOwnerMember, allDeps, years, offeringsReportData, filterMemberName]);

  const loadData = useCallback((isRefresh = false) => {
    if (!activeDerasarId) return;

    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    const requests = [
      Promise.resolve({ data: visibleFinancialYears }),
      getDonationCategories(activeDerasarId),
      getOfferings({ derasarId: activeDerasarId }),
      getReceipts({ derasarId: activeDerasarId }),
    ];

    if (isUserOnly) {
      requests.push(getDerasarPersons(activeDerasarId));
      requests.push(getMyFamily(activeDerasarId));
    } else {
      requests.push(getFamilies(activeDerasarId));
      requests.push(getUsers({ derasarId: activeDerasarId }));
      requests.push(getDerasarPersonsAdmin(activeDerasarId));
      requests.push(getAnonymousReceipts({ derasarId: activeDerasarId }));
    }

    Promise.all(requests)
      .then((results) => {
        const [fyRes, catRes, ofRes, recRes] = results;
        const fyList = fyRes.data || [];
        const allOfferings = ofRes.data || [];
        const allReceipts = recRes.data || [];

        setYears(fyList);
        setCategories(catRes.data || []);

        if (isUserOnly) {
          const deps = results[4]?.data || [];
          const myFamily = results[5]?.data || null;
          const myIds = new Set([String(user.id), ...deps.map((d) => String(d.id ?? d.userId))]);
          const ownOfs = allOfferings.filter((o) => myIds.has(String(o.userId?.id ?? o.userId)));
          const ownRecs = allReceipts.filter((r) => myIds.has(String(r.userId?.id ?? r.userId)));

          setDependents(deps);
          setOwnOfferings(ownOfs);
          setOwnReceipts(ownRecs);

          if (myFamily) {
            const familyIds = new Set(
              (myFamily.members || [])
                .filter((m) => m.status === 'approved')
                .map((m) => String(m.member?.id ?? m.userId))
            );

            if (myFamily.head?.id) {
              familyIds.add(String(myFamily.head.id));
            }

            setOfferings(allOfferings.filter((o) => familyIds.has(String(o.userId?.id ?? o.userId))));
            setReceipts(allReceipts.filter((r) => familyIds.has(String(r.userId?.id ?? r.userId))));
            setFamilies([myFamily]);
          } else {
            setOfferings(ownOfs);
            setReceipts(ownRecs);
            setFamilies([]);
          }

          setSelectedLedgerMember(String(user.id));
        } else {
          setAllDeps(Array.isArray(results[6]?.data) ? results[6].data : []);
          setOfferings(allOfferings);
          setReceipts(allReceipts);
          setFamilies(results[4]?.data || []);
          setAnonymousReceipts(results[7]?.data || []);
        }

        const fyId = String(activeFinancialYearId || '');
        setOpenFyId(fyId);
        setFilterYear(fyId);

        if (isRefresh) {
          toast.success('Reports refreshed');
        }
      })
      .catch(() => toast.error('Failed to load member reports data'))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, [activeDerasarId, isUserOnly, user?.id, visibleFinancialYears, activeFinancialYearId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'offerings' && offeringsReadyRef.current) fetchOfferingsReport();
  }, [activeTab, fetchOfferingsReport, filterYear]);

  useEffect(() => {
    if (!activeDerasarId) return;
    setYears(visibleFinancialYears);
    const fyId = String(activeFinancialYearId || '');
    setOpenFyId(fyId);
    setFilterYear(fyId);
    const { from, to } = getDefaultDatesForFy(fyId, visibleFinancialYears);
    setFilterFrom(from);
    setFilterTo(to);
    setAppliedFrom(from);
    setAppliedTo(to);
    offeringsReadyRef.current = true;
  }, [activeDerasarId, visibleFinancialYears, activeFinancialYearId]);

  const fyId = (o) => o.financialYearId?.id ?? o.financialYearId;
  const matchesFY = (o) => {
    if (!filterYear) return true;
    if (filterYear === 'opening') return fyId(o) == null;
    return String(fyId(o)) === filterYear;
  };

  const fyFilteredOfferings = useMemo(
    () => offerings.filter((o) => matchesFY(o)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offerings, filterYear]
  );

  const reportBaseOfferings = useMemo(() => {
    const inFamily = isUserOnly && families.length > 0;
    const base = inFamily && !showFamilyAll ? ownOfferings : offerings;
    if (!filterYear) return base;
    if (filterYear === 'opening') return base.filter((o) => (o.financialYearId?.id ?? o.financialYearId) == null);
    return base.filter((o) => String(o.financialYearId?.id ?? o.financialYearId) === filterYear);
  }, [isUserOnly, families, showFamilyAll, ownOfferings, offerings, filterYear]);

  const contributionBaseOfferings = useMemo(() => {
    const inFamily = isUserOnly && families.length > 0;
    const base = inFamily && !showFamilyAll ? ownOfferings : offerings;
    if (!filterYear) return base;
    if (filterYear === 'opening') return base.filter((o) => (o.financialYearId?.id ?? o.financialYearId) == null);
    const fy = years.find((y) => String(y.id) === filterYear);
    if (!fy) return base;
    return base.filter((o) => {
      const dates = (o.settlements || [])
        .map((s) => s.Receipt?.receiptDate)
        .filter(Boolean)
        .sort((a, b) => (a > b ? 1 : -1));
      const lastPaid = dates.length > 0 ? dates[dates.length - 1] : null;
      if (!lastPaid) return false;
      return lastPaid >= fy.startDate && (!fy.endDate || lastPaid <= fy.endDate);
    });
  }, [isUserOnly, families, showFamilyAll, ownOfferings, offerings, filterYear, years]);

  const reportBaseReceipts = useMemo(() => {
    const inFamily = isUserOnly && families.length > 0;
    return inFamily && !showFamilyAll ? ownReceipts : receipts;
  }, [isUserOnly, families, showFamilyAll, ownReceipts, receipts]);

  const filteredOfferings = useMemo(() => {
    return reportBaseOfferings
      .filter((o) => !filterCategoryId || String(o.donationCategoryId?.id ?? o.donationCategoryId) === filterCategoryId)
      .filter((o) => !filterMemberId || String(o.userId?.id ?? o.userId) === filterMemberId);
  }, [reportBaseOfferings, filterCategoryId, filterMemberId]);

  const contributionFilteredOfferings = useMemo(() => {
    return contributionBaseOfferings
      .filter((o) => !filterCategoryId || String(o.donationCategoryId?.id ?? o.donationCategoryId) === filterCategoryId)
      .filter((o) => !filterMemberId || String(o.userId?.id ?? o.userId) === filterMemberId);
  }, [contributionBaseOfferings, filterCategoryId, filterMemberId]);

  const ownerMemberOptions = useMemo(() => {
    const seen = new Map();
    allDeps.forEach((dep) => {
      if (dep.createdByMember) {
        const id = String(dep.createdByMember.id);
        if (!seen.has(id)) {
          seen.set(id, {
            id,
            name: dep.createdByMember.name,
            email: dep.createdByMember.auth?.email || '',
            phone: dep.createdByMember.phone || '',
          });
        }
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allDeps]);

  const clearFilters = () => {
    setFilterYear(openFyId);
    setFilterCategoryId('');
    setFilterMemberId('');
    setFilterFrom('');
    setFilterTo('');
    setAppliedFrom('');
    setAppliedTo('');
  };

  const handleRunDateFilter = () => {
    setAppliedFrom(filterFrom);
    setAppliedTo(filterTo);
  };

  const handleFyChange = (value) => {
    const { from, to } = getDefaultDatesForFy(value, years);
    setFilterYear(value);
    setFilterFrom(from);
    setFilterTo(to);
    setAppliedFrom(from);
    setAppliedTo(to);
  };

  const buildCurrentMemberExport = () => {
    const n = (v) => Number(v || 0);
    const base = { activeTab, activeDerasarId, filterYear, yearOptions, filterCategoryId, categories, viewMode };

    // ── Offerings ────────────────────────────────────────────────────────────
    if (activeTab === 'offerings') {
      const all = (offeringsReportData?.rows) || [];
      const rows = (filterMemberName
        ? all.filter((r) => (r.memberName || '').toLowerCase().includes(filterMemberName.toLowerCase()))
        : all
      ).map((r, i) => ({ ...r, no: i + 1 }));
      const active = rows.filter((r) => r.status !== 'cancelled');
      const payload = buildMemberExportPayload({ ...base, title: 'Offerings Report', rows: {
        columns: [
          { key: 'no',            label: 'No.',       type: 'number', align: 'right', weight: 0.5 },
          { key: 'offerNo',       label: 'Offer No',  weight: 0.9 },
          { key: 'offeringDate',  label: 'Date',      type: 'date', weight: 0.9 },
          { key: 'memberName',    label: 'Member',    weight: 1.4 },
          { key: 'category',      label: 'Category',  weight: 1.2 },
          { key: 'offeredAmount', label: 'Offered',   type: 'currency', align: 'right' },
          { key: 'paidAmount',    label: 'Paid',      type: 'currency', align: 'right' },
          { key: 'pendingAmount', label: 'Pending',   type: 'currency', align: 'right' },
          { key: 'status',        label: 'Status',    weight: 0.8 },
        ],
        rows,
        totals: { memberName: 'Total (excl. cancelled)', offeredAmount: active.reduce((s, r) => s + n(r.offeredAmount), 0), paidAmount: active.reduce((s, r) => s + n(r.paidAmount), 0), pendingAmount: active.reduce((s, r) => s + n(r.pendingAmount), 0) },
      } });
      payload.filters = [payload.filters, filterFrom ? `From: ${filterFrom}` : '', filterTo ? `To: ${filterTo}` : ''].filter(Boolean).join('  |  ');
      return payload;
    }

    // ── Contributions ────────────────────────────────────────────────────────
    if (activeTab === 'contributions') {
      const relevant = filteredOfferings.filter((o) => n(o.paidAmount) > 0 && ['partial', 'completed'].includes(o.status));
      if (viewMode === 'summary') {
        const grouped = {};
        relevant.forEach((o) => {
          const key = String(o.userId?.id ?? o.userId);
          if (!grouped[key]) grouped[key] = { memberName: mName(o), offeredAmount: 0, paidAmount: 0 };
          grouped[key].offeredAmount += n(o.offeredAmount);
          grouped[key].paidAmount    += n(o.paidAmount);
        });
        const rows = Object.values(grouped).sort((a, b) => a.memberName.localeCompare(b.memberName)).map((r, i) => ({ ...r, no: i + 1 }));
        const payload = buildMemberExportPayload({ ...base, title: 'Member Contributions', rows: {
          columns: [
            { key: 'no',          label: 'No.',    type: 'number', align: 'right', weight: 0.5 },
            { key: 'memberName', label: 'Member', weight: 1.4 },
            { key: 'offeredAmount', label: 'Total Offered', type: 'currency', align: 'right' },
            { key: 'paidAmount',   label: 'Total Paid',    type: 'currency', align: 'right' },
          ],
          rows,
          totals: { memberName: 'Grand Total', offeredAmount: rows.reduce((s, r) => s + r.offeredAmount, 0), paidAmount: rows.reduce((s, r) => s + r.paidAmount, 0) },
        } });
        payload.filters = [payload.filters, appliedFrom ? `From: ${appliedFrom}` : '', appliedTo ? `To: ${appliedTo}` : ''].filter(Boolean).join('  |  ');
        return payload;
      }
      const rows = relevant.map((o, i) => ({ no: i + 1, memberName: mName(o), category: mCat(o), date: o.offeringDate, offeredAmount: n(o.offeredAmount), paidAmount: n(o.paidAmount) }));
      const payload = buildMemberExportPayload({ ...base, title: 'Member Contributions', rows: {
        columns: [
          { key: 'no',          label: 'No.',        type: 'number', align: 'right', weight: 0.5 },
          { key: 'memberName', label: 'Member',     weight: 1.4 },
          { key: 'category',   label: 'Category',   weight: 1.2 },
          { key: 'date',       label: 'Date',       type: 'date', weight: 0.9 },
          { key: 'offeredAmount', label: 'Offered', type: 'currency', align: 'right' },
          { key: 'paidAmount',    label: 'Paid',    type: 'currency', align: 'right' },
        ],
        rows,
        totals: { memberName: 'Grand Total', offeredAmount: rows.reduce((s, r) => s + r.offeredAmount, 0), paidAmount: rows.reduce((s, r) => s + r.paidAmount, 0) },
      } });
      payload.filters = [payload.filters, appliedFrom ? `From: ${appliedFrom}` : '', appliedTo ? `To: ${appliedTo}` : ''].filter(Boolean).join('  |  ');
      return payload;
    }

    // ── Pending Balance ──────────────────────────────────────────────────────
    if (activeTab === 'pending-balance') {
      const relevant = filteredOfferings.filter((o) => n(o.pendingAmount) > 0 && ['pending', 'partial'].includes(o.status));
      if (viewMode === 'summary') {
        const grouped = {};
        relevant.forEach((o) => {
          const key = String(o.userId?.id ?? o.userId);
          if (!grouped[key]) grouped[key] = { memberName: mName(o), offeredAmount: 0, paidAmount: 0, pendingAmount: 0 };
          grouped[key].offeredAmount  += n(o.offeredAmount);
          grouped[key].paidAmount     += n(o.paidAmount);
          grouped[key].pendingAmount  += n(o.pendingAmount);
        });
        const rows = Object.values(grouped).sort((a, b) => b.pendingAmount - a.pendingAmount).map((r, i) => ({ ...r, no: i + 1 }));
        const payload = buildMemberExportPayload({ ...base, title: 'Pending Balance', rows: {
          columns: [
            { key: 'no',            label: 'No.',           type: 'number', align: 'right', weight: 0.5 },
            { key: 'memberName',    label: 'Member',        weight: 1.4 },
            { key: 'offeredAmount', label: 'Total Offered', type: 'currency', align: 'right' },
            { key: 'paidAmount',    label: 'Total Paid',    type: 'currency', align: 'right' },
            { key: 'pendingAmount', label: 'Total Pending', type: 'currency', align: 'right' },
          ],
          rows,
          totals: { memberName: 'Grand Total', offeredAmount: rows.reduce((s, r) => s + r.offeredAmount, 0), paidAmount: rows.reduce((s, r) => s + r.paidAmount, 0), pendingAmount: rows.reduce((s, r) => s + r.pendingAmount, 0) },
        } });
        payload.filters = [payload.filters, appliedFrom ? `From: ${appliedFrom}` : '', appliedTo ? `To: ${appliedTo}` : ''].filter(Boolean).join('  |  ');
        return payload;
      }
      const rows = relevant.map((o, i) => ({ no: i + 1, memberName: mName(o), category: mCat(o), date: o.offeringDate, offeredAmount: n(o.offeredAmount), paidAmount: n(o.paidAmount), pendingAmount: n(o.pendingAmount), status: o.status }));
      const payload = buildMemberExportPayload({ ...base, title: 'Pending Balance', rows: {
        columns: [
          { key: 'no',            label: 'No.',        type: 'number', align: 'right', weight: 0.5 },
          { key: 'memberName',    label: 'Member',     weight: 1.4 },
          { key: 'category',      label: 'Category',   weight: 1.2 },
          { key: 'date',          label: 'Date',       type: 'date', weight: 0.9 },
          { key: 'offeredAmount', label: 'Offered',    type: 'currency', align: 'right' },
          { key: 'paidAmount',    label: 'Paid',       type: 'currency', align: 'right' },
          { key: 'pendingAmount', label: 'Pending',    type: 'currency', align: 'right' },
          { key: 'status',        label: 'Status',     weight: 0.8 },
        ],
        rows,
        totals: { memberName: 'Grand Total', offeredAmount: rows.reduce((s, r) => s + r.offeredAmount, 0), paidAmount: rows.reduce((s, r) => s + r.paidAmount, 0), pendingAmount: rows.reduce((s, r) => s + r.pendingAmount, 0) },
      } });
      payload.filters = [payload.filters, appliedFrom ? `From: ${appliedFrom}` : '', appliedTo ? `To: ${appliedTo}` : ''].filter(Boolean).join('  |  ');
      return payload;
    }

    // ── Offering Aging ───────────────────────────────────────────────────────
    if (activeTab === 'offering-aging') {
      const relevant = reportBaseOfferings
        .filter((o) => ['pending', 'partial'].includes(o.status) && n(o.pendingAmount) > 0)
        .filter((o) => !filterMemberId || String(o.userId?.id ?? o.userId) === filterMemberId)
        .sort((a, b) => mDays(b) - mDays(a));
      const rows = relevant.map((o, i) => ({ no: i + 1, memberName: mName(o), category: mCat(o), date: o.offeringDate, offeredAmount: n(o.offeredAmount), paidAmount: n(o.paidAmount), pendingAmount: n(o.pendingAmount), daysPending: mDays(o) }));
      const payload = buildMemberExportPayload({ ...base, viewMode: undefined, title: 'Offering Aging', rows: {
        columns: [
          { key: 'no',            label: 'No.',          type: 'number', align: 'right', weight: 0.5 },
          { key: 'memberName',    label: 'Member',       weight: 1.4 },
          { key: 'category',      label: 'Category',     weight: 1.2 },
          { key: 'date',          label: 'Offering Date',type: 'date', weight: 0.9 },
          { key: 'offeredAmount', label: 'Offered',      type: 'currency', align: 'right' },
          { key: 'paidAmount',    label: 'Paid',         type: 'currency', align: 'right' },
          { key: 'pendingAmount', label: 'Pending',      type: 'currency', align: 'right' },
          { key: 'daysPending',   label: 'Days Pending', type: 'number',   align: 'right' },
        ],
        rows,
        totals: { memberName: `Total (${rows.length})`, offeredAmount: rows.reduce((s, r) => s + r.offeredAmount, 0), paidAmount: rows.reduce((s, r) => s + r.paidAmount, 0), pendingAmount: rows.reduce((s, r) => s + r.pendingAmount, 0) },
      } });
      payload.filters = [payload.filters, appliedFrom ? `From: ${appliedFrom}` : '', appliedTo ? `To: ${appliedTo}` : ''].filter(Boolean).join('  |  ');
      return payload;
    }

    // ── Member Ledger ────────────────────────────────────────────────────────
    if (activeTab === 'member-ledger') {
      if (!selectedLedgerMember) { toast.error('Please select a member in the Member Ledger tab first'); return null; }
      const memberLabel = ledgerMemberOptions.find((o) => String(o.id) === String(selectedLedgerMember))?.name || 'Member Ledger';
      const allMemberOfs = offerings.filter((o) => personId(o) === String(selectedLedgerMember));
      const pendingRows = allMemberOfs
        .filter((o) => o.status === 'pending' || o.status === 'partial')
        .map((o) => ({ ...o, _categoryName: mCat(o) }))
        .sort((a, b) => (a.offeringDate || '').localeCompare(b.offeringDate || ''));
      const paidRows = allMemberOfs
        .filter((o) => {
          const fyId = String(o.financialYearId?.id ?? o.financialYearId ?? '');
          return String(activeFinancialYearId) === fyId && n(o.paidAmount) > 0;
        })
        .map((o) => ({ ...o, _categoryName: mCat(o) }));
      const fyLabel = years.find((y) => String(y.id) === String(activeFinancialYearId))?.yearLabel || 'Current Year';
      return {
        derasarId: activeDerasarId,
        title: `Member Ledger - ${memberLabel}`,
        templateName: 'member-ledger',
        tAccount: true,
        rawData: { pending: pendingRows, paid: paidRows },
        filters: `Member: ${memberLabel} | Financial Year: ${fyLabel}`,
        columns: [],
        rows: [],
      };
    }

    // ── Owner Ledger ─────────────────────────────────────────────────────────
    if (activeTab === 'owner-ledger') {
      if (!selectedOwnerMember) { toast.error('Please select a member in the Owner Ledger tab first'); return null; }
      const ownerPersonIds = new Set(allDeps.filter((p) => String(p.createdByMemberId) === String(selectedOwnerMember)).map((p) => String(p.id)));
      const ownerLabel = ownerMemberOptions.find((m) => String(m.id) === String(selectedOwnerMember))?.name || 'Owner';
      const rows = fyFilteredOfferings.filter((o) => ownerPersonIds.has(personId(o))).map((o) => ({ memberName: mName(o), category: mCat(o), date: o.offeringDate, offeredAmount: n(o.offeredAmount), paidAmount: n(o.paidAmount), pendingAmount: n(o.pendingAmount), status: o.status }));
      return buildMemberExportPayload({ ...base, title: `Owner Ledger - ${ownerLabel}`, rows: {
        columns: [
          { key: 'memberName',    label: 'Person',     weight: 1.4 },
          { key: 'category',      label: 'Category',   weight: 1.2 },
          { key: 'date',          label: 'Date',       type: 'date', weight: 0.9 },
          { key: 'offeredAmount', label: 'Offered',    type: 'currency', align: 'right' },
          { key: 'paidAmount',    label: 'Paid',       type: 'currency', align: 'right' },
          { key: 'pendingAmount', label: 'Pending',    type: 'currency', align: 'right' },
          { key: 'status',        label: 'Status',     weight: 0.8 },
        ],
        rows,
        totals: { memberName: 'Grand Total', offeredAmount: rows.reduce((s, r) => s + r.offeredAmount, 0), paidAmount: rows.reduce((s, r) => s + r.paidAmount, 0), pendingAmount: rows.reduce((s, r) => s + r.pendingAmount, 0) },
      } });
    }

    // ── Family Report ────────────────────────────────────────────────────────
    if (activeTab === 'family-report') {
      const familyMap = buildMemberFamilyMap(families);
      const getFamilyIds = (family) => new Set([String(family.head?.id || ''), ...(family.members || []).map((m) => String(m.member?.id ?? m.userId))].filter(Boolean));
      const unassignedOfferings = fyFilteredOfferings.filter((o) => !familyMap[offeringOwner(o).id]);

      if (viewMode === 'summary') {
        const rows = families.map((family) => {
          const ids = getFamilyIds(family);
          const fos = fyFilteredOfferings.filter((o) => ids.has(String(offeringOwner(o).id)));
          return { familyName: family.name, members: ids.size, offeredAmount: fos.reduce((s, o) => s + n(o.offeredAmount), 0), paidAmount: fos.reduce((s, o) => s + n(o.paidAmount), 0), pendingAmount: fos.reduce((s, o) => s + n(o.pendingAmount), 0) };
        });
        const unassignedGroups = {};
        unassignedOfferings.forEach((o) => {
          const owner = offeringOwner(o);
          const { label } = resolveFamilyGroup(owner.id, owner.name, familyMap);
          if (!unassignedGroups[owner.id]) unassignedGroups[owner.id] = { familyName: label, personIds: new Set(), offeredAmount: 0, paidAmount: 0, pendingAmount: 0 };
          unassignedGroups[owner.id].personIds.add(personId(o));
          unassignedGroups[owner.id].offeredAmount += n(o.offeredAmount);
          unassignedGroups[owner.id].paidAmount    += n(o.paidAmount);
          unassignedGroups[owner.id].pendingAmount += n(o.pendingAmount);
        });
        rows.push(...Object.values(unassignedGroups).map(({ personIds, ...g }) => ({ ...g, members: personIds.size })));
        return buildMemberExportPayload({ ...base, title: 'Family Report', rows: {
          columns: [
            { key: 'familyName',    label: 'Family',        weight: 1.8 },
            { key: 'members',       label: 'Members',       type: 'number', align: 'right' },
            { key: 'offeredAmount', label: 'Total Offered', type: 'currency', align: 'right' },
            { key: 'paidAmount',    label: 'Total Paid',    type: 'currency', align: 'right' },
            { key: 'pendingAmount', label: 'Total Pending', type: 'currency', align: 'right' },
          ],
          rows,
          totals: { familyName: 'Grand Total', members: rows.reduce((s, r) => s + r.members, 0), offeredAmount: rows.reduce((s, r) => s + r.offeredAmount, 0), paidAmount: rows.reduce((s, r) => s + r.paidAmount, 0), pendingAmount: rows.reduce((s, r) => s + r.pendingAmount, 0) },
        } });
      }
      const rows = [];
      families.forEach((family) => {
        const ids = getFamilyIds(family);
        fyFilteredOfferings.filter((o) => ids.has(String(offeringOwner(o).id))).forEach((o) => {
          rows.push({ familyName: family.name, memberName: mName(o), category: mCat(o), date: o.offeringDate, offeredAmount: n(o.offeredAmount), paidAmount: n(o.paidAmount), pendingAmount: n(o.pendingAmount), status: o.status });
        });
      });
      unassignedOfferings.forEach((o) => {
        const owner = offeringOwner(o);
        const { label } = resolveFamilyGroup(owner.id, owner.name, familyMap);
        rows.push({ familyName: label, memberName: mName(o), category: mCat(o), date: o.offeringDate, offeredAmount: n(o.offeredAmount), paidAmount: n(o.paidAmount), pendingAmount: n(o.pendingAmount), status: o.status });
      });
      return buildMemberExportPayload({ ...base, title: 'Family Report', rows: {
        columns: [
          { key: 'familyName',    label: 'Family',     weight: 1.2 },
          { key: 'memberName',    label: 'Member',     weight: 1.2 },
          { key: 'category',      label: 'Category',   weight: 1.2 },
          { key: 'date',          label: 'Date',       type: 'date', weight: 0.9 },
          { key: 'offeredAmount', label: 'Offered',    type: 'currency', align: 'right' },
          { key: 'paidAmount',    label: 'Paid',       type: 'currency', align: 'right' },
          { key: 'pendingAmount', label: 'Pending',    type: 'currency', align: 'right' },
          { key: 'status',        label: 'Status',     weight: 0.8 },
        ],
        rows,
        totals: { familyName: 'Grand Total', offeredAmount: rows.reduce((s, r) => s + r.offeredAmount, 0), paidAmount: rows.reduce((s, r) => s + r.paidAmount, 0), pendingAmount: rows.reduce((s, r) => s + r.pendingAmount, 0) },
      } });
    }

    // ── Anonymous Donations ──────────────────────────────────────────────────
    if (activeTab === 'anonymous-donations') {
      const rows = anonymousReceipts
        .filter((r) => !filterYear || String(r.financialYearId?.id ?? r.financialYearId) === String(filterYear))
        .sort((a, b) => new Date(b.receiptDate) - new Date(a.receiptDate))
        .map((r) => ({ receiptDate: r.receiptDate, receiptNo: r.receiptNo, donorName: r.donorName || 'Shah A/C', category: r.donationCategory?.name || '-', paymentMode: r.paymentMode, amountInr: n(r.amountInr) }));
      return buildMemberExportPayload({ ...base, title: 'Shah A/C Donations', rows: {
        columns: [
          { key: 'receiptDate', label: 'Date',        type: 'date', weight: 0.9 },
          { key: 'receiptNo',   label: 'Receipt No',  weight: 0.9 },
          { key: 'donorName',   label: 'Donor Name',  weight: 1.4 },
          { key: 'category',    label: 'Category',    weight: 1.2 },
          { key: 'paymentMode', label: 'Mode',        weight: 0.8 },
          { key: 'amountInr',   label: 'Amount',      type: 'currency', align: 'right' },
        ],
        rows,
        totals: { donorName: 'Total', amountInr: rows.reduce((s, r) => s + r.amountInr, 0) },
      } });
    }

    // ── Receipts Book (full-info) ────────────────────────────────────────────
    const fbGetCatAmt = (r, catId) => {
      if (r.pledgeSettlements?.length > 0) {
        const sum = r.pledgeSettlements.reduce((t, s) => {
          const scId = s.Offering?.donationCategoryId ?? s.offering?.donationCategoryId;
          return String(scId) === String(catId) ? t + (parseFloat(s.settledAmount) || 0) : t;
        }, 0);
        if (sum > 0) return sum;
      }
      const rCatId = r.donationCategoryId?.id ?? r.donationCategoryId ?? r.DonationCategory?.id;
      return rCatId && String(rCatId) === String(catId) ? parseFloat(r.amountInr) || 0 : 0;
    };
    const fbReceipts = reportBaseReceipts
      .filter((r) => !filterYear || String(r.financialYearId?.id ?? r.financialYearId) === String(filterYear))
      .filter((r) => !filterMemberId || String(r.userId?.id ?? r.userId) === filterMemberId)
      .filter((r) => !filterCategoryId || String(r.donationCategoryId?.id ?? r.donationCategoryId) === filterCategoryId)
      .sort((a, b) => new Date(b.receiptDate) - new Date(a.receiptDate));
    const rows = fbReceipts.map((r) => {
      const row = { receiptDate: r.receiptDate, receiptNo: r.receiptNo, memberName: r.member?.name || r.donorName || '-', paymentMode: r.paymentMode, amountInr: n(r.amountInr) };
      categories.forEach((cat) => { row[`cat_${cat.id}`] = fbGetCatAmt(r, cat.id); });
      return row;
    });
    const catTotals = categories.reduce((acc, cat) => ({ ...acc, [`cat_${cat.id}`]: rows.reduce((s, r) => s + (r[`cat_${cat.id}`] || 0), 0) }), {});
    return buildMemberExportPayload({ ...base, title: 'Receipts Book', rows: {
      columns: [
        { key: 'receiptDate', label: 'Date',       type: 'date',     weight: 0.9 },
        { key: 'receiptNo',   label: 'Receipt No', weight: 0.9 },
        { key: 'memberName',  label: 'Member',     weight: 1.4 },
        ...categories.map((cat) => ({ key: `cat_${cat.id}`, label: cat.name, type: 'currency', align: 'right' })),
        { key: 'paymentMode', label: 'Mode',       weight: 0.8 },
        { key: 'amountInr',   label: 'Total',      type: 'currency', align: 'right' },
      ],
      rows,
      totals: { receiptNo: 'Total', ...catTotals, amountInr: rows.reduce((s, r) => s + r.amountInr, 0) },
    } });
  };

  const handleDownloadExport = async (format) => {
    const payload = buildCurrentMemberExport();
    if (!payload) return;

    try {
      const res = await exportReportTable({ ...payload, format });
      const type = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeTab}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to download ${format === 'excel' ? 'Excel' : 'PDF'}`);
    }
  };

  if (!activeDerasarId) {
    return <div className="empty-state"><p>Select a derasar to view member reports</p></div>;
  }

  const isMemberLedger = activeTab === 'member-ledger';
  const isOwnerLedger  = activeTab === 'owner-ledger';
  const isFamilyReport = activeTab === 'family-report';
  const isOfferingAging = activeTab === 'offering-aging';

  // PDF and Excel require a member to be selected for these two tabs
  const needsMemberSelection = (isMemberLedger && !selectedLedgerMember) || (isOwnerLedger && !selectedOwnerMember);

  return (
    <div className="report-page" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        icon={<FiBarChart2 />}
        title="Member Reports"
        actions={(
          <div className={styles.printHide} style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => loadData(true)}
              disabled={refreshing || loading}
              title="Re-fetch all report data from backend"
            >
              <FiRefreshCw size={14} style={refreshing ? { animation: 'spin 0.8s linear infinite' } : undefined} />
              {refreshing ? 'Refreshing...' : 'Run Report'}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                if (pdfHasDefaults && activePdfType && memberPdfProps) downloadPdf(memberPdfProps);
                else setShowPdfModal(true);
              }}
              disabled={!activePdfType || pdfDownloading || needsMemberSelection}
              title={needsMemberSelection ? 'Select a member first' : pdfHasDefaults ? 'Download PDF using saved defaults' : 'Configure and export PDF'}
            >
              <FiPrinter size={14} /> {pdfDownloading ? 'Generating…' : 'PDF'}
            </button>
            {pdfHasDefaults && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowPdfModal(true)}
                disabled={!activePdfType || needsMemberSelection}
                title={needsMemberSelection ? 'Select a member first' : 'PDF options'}
              >
                <FiSettings size={14} />
              </button>
            )}
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowExcelModal(true)}
              disabled={needsMemberSelection}
              title={needsMemberSelection ? 'Select a member first' : undefined}
            >
              <FiDownload size={14} /> Excel
            </button>
          </div>
        )}
      />

      <div className={styles.tabBar}>
        {TABS.filter((tab) => !tab.adminOnly || !isUserOnly).map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'offerings' && (
        <div className={`${styles.filterRow} ${styles.printHide}`}>
          <div className="form-group">
            <label>Financial Year</label>
            <select value={filterYear} onChange={(e) => handleFyChange(e.target.value)}>
              <option value="">All Years</option>
              <option value="opening">Opening</option>
              {yearOptions.map((y) => (
                <option key={y.id} value={String(y.id)}>{y.yearLabel}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>From Date</label>
            <input type="date" value={filterFrom} min={fyMinDate || undefined} max={fyMaxDate || undefined} onChange={(e) => setFilterFrom(e.target.value)} />
          </div>
          <div className="form-group">
            <label>To Date</label>
            <input type="date" value={filterTo} min={fyMinDate || undefined} max={fyMaxDate || undefined} onChange={(e) => setFilterTo(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select value={offeringsStatus} onChange={(e) => setOfferingsStatus(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="pending_partial">Pending + Partial</option>
              <option value="completed">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="form-group" style={{ minWidth: 200 }}>
            <label>Member</label>
            {(() => {
              const allOpt = { id: '', name: 'All Members', email: '', phone: '' };
              const sel = filterMemberId ? ledgerMemberOptions.find((m) => String(m.id) === filterMemberId) || allOpt : allOpt;
              return (
                <OfferingsMemberSelect
                  compact
                  value={filterMemberId}
                  displayValue={sel.name}
                  displayEmail={sel.email || ''}
                  displayPhone={sel.phone || ''}
                  options={[allOpt, ...ledgerMemberOptions]}
                  onSelect={(m) => setFilterMemberId(String(m.id))}
                />
              );
            })()}
          </div>
          <div className="form-group" style={{ alignSelf: 'flex-end' }}>
            <button className="btn btn-primary btn-sm" onClick={fetchOfferingsReport} disabled={offeringsLoading}>
              Run
            </button>
          </div>
        </div>
      )}

      {activeTab !== 'offerings' && <div className={`${styles.filterRow} ${styles.printHide}`}>
        <div className="form-group">
          <label>Financial Year</label>
          <select
            value={filterYear}
            onChange={(e) => handleFyChange(e.target.value)}
          >
            <option value="">All Years</option>
            <option value="opening">Opening</option>
            {yearOptions.map((year) => (
              <option key={year.id} value={String(year.id)}>{year.yearLabel}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>From</label>
          <input
            type="date"
            value={filterFrom}
            min={fyMinDate || undefined}
            max={fyMaxDate || filterTo || undefined}
            onChange={(e) => setFilterFrom(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>To</label>
          <input
            type="date"
            value={filterTo}
            min={fyMinDate || filterFrom || undefined}
            max={fyMaxDate || undefined}
            onChange={(e) => setFilterTo(e.target.value)}
          />
        </div>

        {!isMemberLedger && !isOwnerLedger && !isFamilyReport && activeTab !== 'anonymous-donations' && (
          <>
            <div className="form-group">
              <label>Category</label>
              <select value={filterCategoryId} onChange={(e) => setFilterCategoryId(e.target.value)}>
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={String(category.id)}>{category.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ minWidth: 200 }}>
              <label>Member</label>
              {(() => {
                const allOpt = { id: '', name: 'All Members', email: '', phone: '' };
                const sel = filterMemberId ? ledgerMemberOptions.find((m) => String(m.id) === filterMemberId) || allOpt : allOpt;
                return (
                  <OfferingsMemberSelect
                    compact
                    value={filterMemberId}
                    displayValue={sel.name}
                    displayEmail={sel.email || ''}
                    displayPhone={sel.phone || ''}
                    options={[allOpt, ...ledgerMemberOptions]}
                    onSelect={(m) => setFilterMemberId(String(m.id))}
                  />
                );
              })()}
            </div>
          </>
        )}

        {isMemberLedger && (
          <div className="form-group" style={{ minWidth: 200 }}>
            <label>{isUserOnly ? 'View Ledger For' : 'Select Member'}</label>
            {(() => {
              const sel = ledgerMemberOptions.find((m) => String(m.id) === String(selectedLedgerMember));
              return (
                <OfferingsMemberSelect
                  compact
                  value={selectedLedgerMember}
                  displayValue={sel?.name || ''}
                  displayEmail={sel?.email || ''}
                  displayPhone={sel?.phone || ''}
                  options={ledgerMemberOptions}
                  onSelect={(m) => setSelectedLedgerMember(String(m.id))}
                />
              );
            })()}
          </div>
        )}

        {isOwnerLedger && (
          <div className="form-group" style={{ minWidth: 200 }}>
            <label>Select Member (Owner)</label>
            {(() => {
              const sel = ownerMemberOptions.find((m) => String(m.id) === String(selectedOwnerMember));
              return (
                <OfferingsMemberSelect
                  compact
                  value={selectedOwnerMember}
                  displayValue={sel?.name || ''}
                  displayEmail={sel?.email || ''}
                  displayPhone={sel?.phone || ''}
                  options={ownerMemberOptions}
                  onSelect={(m) => setSelectedOwnerMember(String(m.id))}
                />
              );
            })()}
          </div>
        )}

        <div className="form-group" style={{ minWidth: 'unset', flexShrink: 0 }}>
          <label style={{ visibility: 'hidden' }}>Run</label>
          <button className="btn btn-primary btn-sm" onClick={handleRunDateFilter}>
            Run
          </button>
        </div>

        <div className="form-group" style={{ minWidth: 'unset', flexShrink: 0 }}>
          <label style={{ visibility: 'hidden' }}>Clear</label>
          <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
            <FiX size={13} /> Clear
          </button>
        </div>

        {!isMemberLedger && !isOwnerLedger && <div style={{ flex: 1 }} />}

        {!isMemberLedger && !isOwnerLedger && (
          <div className={styles.rightControls}>
            {isUserOnly && families.length > 0 && !isFamilyReport && (
              <>
                <label className={styles.familyToggleWrap}>
                  <input
                    type="checkbox"
                    checked={showFamilyAll}
                    onChange={(e) => setShowFamilyAll(e.target.checked)}
                  />
                  <span className={`${styles.toggleTrack} ${showFamilyAll ? styles.toggleTrackOn : ''}`} />
                  Show all family members
                </label>
                <span className={styles.rightDivider} />
              </>
            )}

            {!isOfferingAging && (
              <div className={styles.viewToggle}>
                <button
                  className={`btn btn-sm ${viewMode === 'detail' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setViewMode('detail')}
                >
                  Detail
                </button>
                <button
                  className={`btn btn-sm ${viewMode === 'summary' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setViewMode('summary')}
                >
                  Summary
                </button>
              </div>
            )}
          </div>
        )}
      </div>}

      {activeTab === 'offerings' ? (
        offeringsLoading
          ? <div className="loading"><div className="spinner" /></div>
          : <OfferingsReport data={offeringsReportData} filterMemberName={filterMemberName} />
      ) : loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isMemberLedger ? (
        <MemberLedgerReport
          offerings={offerings}
          currentFyId={activeFinancialYearId}
          selectedMemberId={selectedLedgerMember}
          isUserOnly={isUserOnly}
        />
      ) : isOwnerLedger ? (
        <OwnerLedgerReport
          offerings={offerings}
          allDeps={allDeps}
          selectedMemberId={selectedOwnerMember}
          currentFyId={activeFinancialYearId}
        />
      ) : isFamilyReport ? (
        <FamilyReport offerings={fyFilteredOfferings} families={families} viewMode={viewMode} />
      ) : activeTab === 'contributions' ? (
        <MemberContributionsReport offerings={contributionFilteredOfferings} viewMode={viewMode} memberOptions={ledgerMemberOptions} filterFrom={appliedFrom} filterTo={appliedTo} />
      ) : activeTab === 'offering-aging' ? (
        <OfferingAgingReport
          offerings={reportBaseOfferings}
          filterCategoryId={filterCategoryId}
          filterMemberId={filterMemberId}
          categories={categories}
        />
      ) : activeTab === 'anonymous-donations' ? (
        <AnonymousDonationsReport receipts={anonymousReceipts} filterYear={filterYear} categories={categories} />
      ) : activeTab === 'full-info' ? (
        <FullInfoReport
          receipts={reportBaseReceipts}
          categories={categories}
          filterMemberId={filterMemberId}
          filterYear={filterYear}
          filterCategoryId={filterCategoryId}
          memberOptions={ledgerMemberOptions}
          viewMode={viewMode}
        />
      ) : (
        <PendingBalanceReport offerings={filteredOfferings} viewMode={viewMode} memberOptions={ledgerMemberOptions} filterFrom={appliedFrom} filterTo={appliedTo} />
      )}

      {showPdfModal && memberPdfProps && activePdfType && (
        <PdfOptionsModal
          type={activePdfType}
          documentProps={memberPdfProps}
          onClose={() => setShowPdfModal(false)}
        />
      )}

      {showExcelModal && buildCurrentMemberExport() && (
        <ExcelExportModal
          onClose={() => setShowExcelModal(false)}
          onDownload={() => handleDownloadExport('excel')}
          exportPayload={buildCurrentMemberExport() || {}}
          defaultEmailTo=""
        />
      )}

    </div>
  );
};

export default MemberReportsPage;
